import { apiClient } from './ApiClient';

/**
 * Base service class providing common CRUD operations and utilities
 */
export class BaseService {
  constructor(baseEndpoint, options = {}) {
    this.baseEndpoint = baseEndpoint;
    this.apiClient = options.apiClient || apiClient;
    this.defaultConfig = options.defaultConfig || {};
  }

  /**
   * Build URL with optional ID
   */
  buildUrl(id = null, suffix = '') {
    let url = this.baseEndpoint;
    if (id) url += `/${id}`;
    if (suffix) url += `/${suffix}`;
    return url;
  }

  /**
   * Get all resources with optional filtering and pagination
   */
  async getAll(params = {}) {
    const queryString = this.apiClient.buildQuery(params);
    const url = queryString ? `${this.baseEndpoint}?${queryString}` : this.baseEndpoint;
    return this.apiClient.get(url);
  }

  /**
   * Get paginated resources
   */
  async getPaginated(params = {}) {
    return this.apiClient.getPaginated(this.baseEndpoint, params);
  }

  /**
   * Get single resource by ID
   */
  async getById(id) {
    if (!id) throw new Error('ID is required');
    return this.apiClient.get(this.buildUrl(id));
  }

  /**
   * Create new resource
   */
  async create(data) {
    if (!data) throw new Error('Data is required for creation');
    return this.apiClient.post(this.baseEndpoint, data);
  }

  /**
   * Update existing resource
   */
  async update(id, data) {
    if (!id) throw new Error('ID is required for update');
    if (!data) throw new Error('Data is required for update');
    return this.apiClient.put(this.buildUrl(id), data);
  }

  /**
   * Partially update resource
   */
  async patch(id, data) {
    if (!id) throw new Error('ID is required for patch');
    if (!data) throw new Error('Data is required for patch');
    return this.apiClient.patch(this.buildUrl(id), data);
  }

  /**
   * Delete resource
   */
  async delete(id) {
    if (!id) throw new Error('ID is required for deletion');
    return this.apiClient.delete(this.buildUrl(id));
  }

  /**
   * Bulk operations
   */
  async bulkCreate(items) {
    if (!Array.isArray(items)) throw new Error('Items must be an array');
    return this.apiClient.post(`${this.baseEndpoint}/bulk`, { items });
  }

  async bulkUpdate(ids, data) {
    if (!Array.isArray(ids)) throw new Error('IDs must be an array');
    if (!data) throw new Error('Data is required for bulk update');
    return this.apiClient.patch(`${this.baseEndpoint}/bulk`, { ids, data });
  }

  async bulkDelete(ids) {
    if (!Array.isArray(ids)) throw new Error('IDs must be an array');
    return this.apiClient.delete(`${this.baseEndpoint}/bulk`, { data: { ids } });
  }

  /**
   * Search resources
   */
  async search(query, params = {}) {
    if (!query) throw new Error('Search query is required');
    const searchParams = { search: query, ...params };
    return this.getAll(searchParams);
  }

  /**
   * Get resource statistics
   */
  async getStats(params = {}) {
    const queryString = this.apiClient.buildQuery(params);
    const url = queryString ? `${this.baseEndpoint}/stats?${queryString}` : `${this.baseEndpoint}/stats`;
    return this.apiClient.get(url);
  }

  /**
   * Export resources
   */
  async export(params = {}, format = 'csv') {
    const queryString = this.apiClient.buildQuery({ ...params, format });
    const url = `${this.baseEndpoint}/export?${queryString}`;
    return this.apiClient.download(url, `export_${Date.now()}.${format}`);
  }

  /**
   * Upload file
   */
  async uploadFile(id, file, field = 'file') {
    const formData = new FormData();
    formData.append(field, file);
    return this.apiClient.upload(this.buildUrl(id, 'upload'), formData);
  }

  /**
   * Generic action method
   */
  async action(id, actionName, data = {}) {
    if (!id) throw new Error('ID is required for action');
    if (!actionName) throw new Error('Action name is required');
    return this.apiClient.post(this.buildUrl(id, actionName), data);
  }

  /**
   * Get related resources
   */
  async getRelated(id, relation, params = {}) {
    if (!id) throw new Error('ID is required');
    if (!relation) throw new Error('Relation name is required');
    const queryString = this.apiClient.buildQuery(params);
    const url = queryString 
      ? `${this.buildUrl(id, relation)}?${queryString}` 
      : this.buildUrl(id, relation);
    return this.apiClient.get(url);
  }

  /**
   * Batch operations helper
   */
  async batchOperations(operations) {
    return this.apiClient.batch(operations);
  }

  /**
   * Custom endpoint helper
   */
  async customEndpoint(endpoint, method = 'GET', data = null, params = {}) {
    const url = `${this.baseEndpoint}/${endpoint}`;
    
    switch (method.toUpperCase()) {
      case 'GET': {
        const queryString = this.apiClient.buildQuery(params);
        return this.apiClient.get(queryString ? `${url}?${queryString}` : url);
      }
      case 'POST':
        return this.apiClient.post(url, data);
      case 'PUT':
        return this.apiClient.put(url, data);
      case 'PATCH':
        return this.apiClient.patch(url, data);
      case 'DELETE':
        return this.apiClient.delete(url);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }
}

export default BaseService;